/**
 * File extraction — the only layer aware of the spreadsheet format.
 * Parses an uploaded file into a neutral { headers, rows } shape so the
 * mapping/validation layers stay format-agnostic.
 */

const MAX_DATA_ROWS = 2000;

/**
 * Thrown for problems that make the whole file unreadable (corrupt file,
 * no worksheet, too many rows). Carries a user-facing message.
 */
export class ImportFileError extends Error {}

/**
 * Sentinel wrapper for cells containing spreadsheet error values (#N/A, #REF!, …)
 * so the validator can report them per cell instead of importing garbage.
 *
 * @param {*} value Normalised cell value
 * @returns {boolean}
 */
export const isCellError = (value) => {
  return !!value && typeof value === 'object' && '__cellError' in value;
};

/**
 * Normalises an ExcelJS cell value to a plain string/number/boolean/null.
 * Handles rich text, formula results, hyperlinks, dates, and error values.
 */
const cellToValue = (value) => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object') {
    if (value.richText) {
      return cellToValue(value.richText.map(run => run.text).join(''));
    }

    if ('error' in value) {
      return { __cellError: value.error };
    }

    if ('formula' in value || 'sharedFormula' in value) {
      return cellToValue(value.result ?? null);
    }

    if ('hyperlink' in value) {
      return cellToValue(value.text ?? value.hyperlink);
    }

    if (value instanceof Date) {
      return String(value);
    }

    // Unknown object shape — fall back to its text representation
    return cellToValue(String(value));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  return value;
};

/**
 * Parses an .xlsx file into a neutral table shape.
 *
 * ExcelJS is loaded on demand so it only enters the bundle when the batch
 * upload feature is actually used.
 *
 * @param {File} file Uploaded spreadsheet
 * @returns {Promise<{ headers: string[], rows: Array<{ excelRow: number, cells: any[] }> }>}
 * @throws {ImportFileError} When the file cannot be read as a spreadsheet
 */
export const extractTable = async (file) => {
  const mod = await import('exceljs');
  const ExcelJS = mod.default ?? mod;

  const workbook = new ExcelJS.Workbook();

  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch {
    throw new ImportFileError('Could not read this file. Please upload a valid .xlsx spreadsheet.');
  }

  const worksheet = workbook.worksheets[0];

  if (!worksheet) {
    throw new ImportFileError('The spreadsheet contains no worksheets.');
  }

  const headers = [];

  worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const value = cellToValue(cell.value);
    headers[colNumber - 1] = isCellError(value) ? null : (value === null ? null : String(value));
  });

  // Drop trailing empty header cells but keep interior gaps positional
  while (headers.length && headers[headers.length - 1] === null) {
    headers.pop();
  }

  const rows = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const cells = headers.map((_, index) => cellToValue(row.getCell(index + 1).value));

    if (cells.every(cell => cell === null)) return;

    rows.push({ excelRow: rowNumber, cells });
  });

  if (rows.length > MAX_DATA_ROWS) {
    throw new ImportFileError(`The file has too many rows — the limit is ${MAX_DATA_ROWS} data rows per upload.`);
  }

  return { headers, rows };
};
